/**
 * Composants de pagination branchés dans reactive-json
 * (`customPlugins.pagination`, consommés par le hook `usePagination` que le
 * `Switch` paginé appelle).
 *
 * Ils portent les MÊMES tokens que la barre de pagination des tables
 * (`Pagination.jsx`, recette du DS Cosmos) : même gabarit 34 px, même bordure
 * slate, même page courante en `iris-600`. Les deux barres coexistent à
 * l'écran — l'une pilotée par un serveur paginé, l'autre par un `Switch`
 * paginé — et rien ne justifierait qu'elles ne se ressemblent pas.
 *
 * Accessibilité (RGAA 4.1.2 / WCAG 2.2) : `<nav>` nommé, un libellé explicite
 * par bouton, page courante en `aria-current="page"`, chevrons neutralisés
 * (`aria-hidden`) — le nom du bouton vient de son `aria-label`, pas du signe.
 */
const PAGE_BASE =
    'min-w-[34px] h-[34px] inline-flex items-center justify-center px-2 rounded-md border '
    + 'text-sm font-semibold cursor-pointer transition-colors duration-150';
const PAGE_IDLE = 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50';
const PAGE_ARROW =
    'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 '
    + 'disabled:opacity-40 disabled:cursor-not-allowed';
const PAGE_ACTIVE = 'bg-iris-600 border-transparent text-white shadow-[var(--shadow-primary)]';

export const PaginationContainer = ({ children, ...props }) => {
    return (
        <nav aria-label="Pagination" {...props}>
            <div className="flex justify-center items-center gap-1.5 font-sans">{children}</div>
        </nav>
    );
};

const ArrowButton = ({ disabled, onClick, label, sign, children, ...props }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`${PAGE_BASE} ${PAGE_ARROW}`}
        aria-label={label}
        {...props}
    >
        <span aria-hidden="true">{children ?? sign}</span>
    </button>
);

export const PaginationFirst = (props) => <ArrowButton {...props} label="Première page" sign="«" />;
export const PaginationPrev = (props) => <ArrowButton {...props} label="Page précédente" sign="‹" />;
export const PaginationNext = (props) => <ArrowButton {...props} label="Page suivante" sign="›" />;
export const PaginationLast = (props) => <ArrowButton {...props} label="Dernière page" sign="»" />;

export const PaginationItem = ({ active, disabled, onClick, children, ...props }) => {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            aria-current={active ? 'page' : undefined}
            aria-label={`Page ${children}`}
            className={`${PAGE_BASE} ${active ? PAGE_ACTIVE : PAGE_IDLE}`}
            {...props}
        >
            {children}
        </button>
    );
};

export const PaginationEllipsis = ({ children, ...props }) => {
    return (
        <span
            className="min-w-[34px] text-center text-slate-400 select-none"
            aria-hidden="true"
            {...props}
        >
            {children ?? '…'}
        </span>
    );
};
